import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import { useUserContext } from "api/user/UserProvider";
import {
  DELIVERY_NOTE_PDF_NAME_MODES,
  HTTP_STATUS_CODE,
  POPUP_STATUS,
  QUERY_STATUS,
} from "constants/constants";
import {
  DELIVERY_NOTE_COLUMN_REQUIRED,
  DELIVERY_NOTE_IMAGE_IMPORT_FAILED,
  DELIVERY_NOTE_TEMPLATE_NAME_ALREADY_EXISTS,
  DELIVERY_NOTE_TEMPLATE_NOT_FOUND,
  DELIVERY_NOTE_TEMPLATES_LOAD_ERROR,
  FIELD_REQUIRED,
} from "constants/popup-codes";
import { Typography } from "core/typography/Typography";
import {
  DELIVERY_NOTE_CREATE_HEADER,
  DELIVERY_NOTE_IMAGE_FIELDS,
  DELIVERY_NOTE_SUBMIT_LABELS,
} from "constants/forms";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useDeliveryNoteTemplateContext } from "api/delivery-note/DeliveryNoteTemplateProvider";
import {
  getDeliveryNoteImageQuery,
  uploadDeliveryNoteImageQuery,
} from "api/delivery-note/queries";
import { useApiFieldErrors } from "utils/useApiFieldErrors";
import {
  buildFormValuesFromTemplate,
  getCurrencySymbol,
  getOfferedCurrencies,
  getSignatoryName,
  isTemplateNameTaken,
  resizeImageToBlob,
} from "utils/deliveryNoteHelper";
import IdentityBlock from "./blocks/IdentityBlock";
import IssuerBlock from "./blocks/IssuerBlock";
import RecipientBlock from "./blocks/RecipientBlock";
import ColumnsBlock from "./blocks/ColumnsBlock";
import SignatoriesBlock from "./blocks/SignatoriesBlock";
import DeliveryNoteTemplateLivePreview from "./DeliveryNoteTemplateLivePreview";

const NAME_FOCUS_ID = "input-text-delivery-note-name";

const DeliveryNoteTemplateForm = ({ onCancel, onSaveStatus, template }) => {
  const intl = useIntl();
  const { setCode, setStatus } = useContext(InfoContext);
  const { setApiErrorResponse } = useUserContext();
  const { currencyListGetter, currencyList } = useCurrencyContext();
  const {
    getDeliveryNoteTemplates,
    createDeliveryNoteTemplate,
    updateDeliveryNoteTemplate,
  } = useDeliveryNoteTemplateContext();

  const isEditing = !!template?.id;
  const initialValues = useMemo(
    () => buildFormValuesFromTemplate(template),
    [template]
  );

  const {
    control,
    register,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState: { errors, isSubmitted },
  } = useForm({ defaultValues: initialValues });

  useApiFieldErrors(setError);

  const [isSaving, setIsSaving] = useState(false);
  const isSubmittingRef = useRef(false);

  const [imageUrlByFileId, setImageUrlByFileId] = useState({});
  const imageRequestIdsRef = useRef({});
  const imageFileIds = watch(DELIVERY_NOTE_IMAGE_FIELDS);

  const keepImageUrls = (keptFileIds) =>
    setImageUrlByFileId((urls) => {
      const kept = Object.entries(urls).filter(([fileId]) =>
        keptFileIds.includes(fileId)
      );
      return kept.length === Object.keys(urls).length
        ? urls
        : Object.fromEntries(kept);
    });

  const isCurrentImage = (fileId) =>
    getValues(DELIVERY_NOTE_IMAGE_FIELDS).includes(fileId);

  const cacheImageUrl = (fileId, url) =>
    setImageUrlByFileId((urls) => ({ ...urls, [fileId]: url }));

  const loadImageUrl = (fileId) =>
    getDeliveryNoteImageQuery({ fileId })
      .then((url) => {
        if (isCurrentImage(fileId)) cacheImageUrl(fileId, url);
      })
      .catch(() => {
        if (isCurrentImage(fileId)) {
          raiseError(DELIVERY_NOTE_IMAGE_IMPORT_FAILED);
        }
      });

  useEffect(() => {
    const currentFileIds = imageFileIds.filter(Boolean);
    keepImageUrls(currentFileIds);
    currentFileIds.forEach(loadImageUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFileIds.join("|")]);

  const resolveImage = (fileId) => (fileId && imageUrlByFileId[fileId]) || "";

  useEffect(() => {
    currencyListGetter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const offeredCurrencies = useMemo(
    () => getOfferedCurrencies(currencyList),
    [currencyList]
  );

  useEffect(() => {
    if (getValues("currency_id")) return;
    const [firstCurrency] = offeredCurrencies;
    if (firstCurrency) setValue("currency_id", firstCurrency.id);
  }, [offeredCurrencies, getValues, setValue]);

  const raiseError = (code) => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(code);
  };

  const reportSaveFailure = ({ status, fieldErrors }) => {
    const [field, errorInfo] = Object.entries(fieldErrors || {})[0] || [];
    const messageId = errorInfo?.translationKey;
    if (messageId && intl.messages[messageId]) {
      onSaveStatus?.(QUERY_STATUS.INITIAL);
      raiseError(messageId);
      if (field === "name") document.getElementById(NAME_FOCUS_ID)?.focus();
      return;
    }
    const notFound = isEditing && status === HTTP_STATUS_CODE.NOT_FOUND;
    onSaveStatus?.(
      QUERY_STATUS.ERROR,
      notFound
        ? {
            errorMessageId: DELIVERY_NOTE_TEMPLATE_NOT_FOUND,
            errorMessageDefault:
              "Ce modèle n'existe plus, il a peut-être été supprimé.",
          }
        : undefined
    );
  };

  const currencySymbol = getCurrencySymbol({
    currencyList,
    currencyId: watch("currency_id"),
  });

  const handleImageChange = async (field, file) => {
    const requestId = (imageRequestIdsRef.current[field] || 0) + 1;
    imageRequestIdsRef.current[field] = requestId;
    const isCurrentRequest = () =>
      imageRequestIdsRef.current[field] === requestId;

    if (!file) {
      setValue(field, null);
      return;
    }
    try {
      const blob = await resizeImageToBlob({ file });
      const fileId = await uploadDeliveryNoteImageQuery({ blob });
      if (!fileId) throw new Error("Upload returned no file id");
      if (isCurrentRequest()) setValue(field, fileId);
    } catch (e) {
      console.error("handleImageChange", e);
      if (!isCurrentRequest()) return;
      setValue(field, null);
      raiseError(DELIVERY_NOTE_IMAGE_IMPORT_FAILED);
    }
  };

  const buildPayload = () => {
    const values = getValues();
    const signatoryName = (party) =>
      getSignatoryName({ source: values, party, intl });

    return {
      ...template,
      name: values.name.trim(),
      title: values.title.trim(),
      subtitle: values.subtitle,
      currency_id: values.currency_id,
      pdf_file_name:
        values.pdf_file_name_mode === DELIVERY_NOTE_PDF_NAME_MODES.CUSTOM
          ? values.pdf_file_name.trim()
          : "",
      pdf_suffix_enabled: values.pdf_suffix_enabled,
      issuer: {
        ...template?.issuer,
        logo_file_id: values.issuer_logo_file_id,
        company_id: values.issuer_company?.[0]?.value,
        company_name: values.issuer_company?.[0]?.label,
        address: values.issuer_address,
        siret: values.issuer_siret,
        phone: values.issuer_phone,
      },
      recipient: {
        ...template?.recipient,
        logo_file_id: values.recipient_logo_file_id,
        company_id: values.recipient_company?.[0]?.value,
        company_name: values.recipient_company?.[0]?.label,
        address: values.recipient_address,
        siret: values.recipient_siret,
        phone: values.recipient_phone,
      },
      columns: values.columns,
      issuer_signature_file_id: values.issuer_signature_file_id,
      issuer_signatory_name: signatoryName("issuer"),
      recipient_signature_file_id: values.recipient_signature_file_id,
      recipient_signatory_name: signatoryName("recipient"),
    };
  };

  const saveTemplate = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSaving(true);

    try {
      clearErrors();
      setApiErrorResponse(null);
      if (!getValues("columns")?.length) {
        raiseError(DELIVERY_NOTE_COLUMN_REQUIRED);
        return;
      }
      const templates = await getDeliveryNoteTemplates();
      if (!templates) {
        raiseError(DELIVERY_NOTE_TEMPLATES_LOAD_ERROR);
        return;
      }
      if (
        isTemplateNameTaken({
          name: getValues("name"),
          templates,
          excludedId: template?.id,
        })
      ) {
        raiseError(DELIVERY_NOTE_TEMPLATE_NAME_ALREADY_EXISTS);
        setError("name", { type: "required" });
        document.getElementById(NAME_FOCUS_ID)?.focus();
        return;
      }

      const payload = buildPayload();
      onSaveStatus?.(QUERY_STATUS.LOADING);
      const failure = isEditing
        ? await updateDeliveryNoteTemplate({ id: template.id, data: payload })
        : await createDeliveryNoteTemplate(payload);
      if (failure) {
        reportSaveFailure(failure);
        return;
      }

      onSaveStatus?.(QUERY_STATUS.SUCCESS);
    } finally {
      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  };

  const submitForm = rhfHandleSubmit(saveTemplate, () =>
    raiseError(FIELD_REQUIRED)
  );

  const submitLabel =
    DELIVERY_NOTE_SUBMIT_LABELS[isEditing ? "update" : "create"];

  const blockProps = {
    control,
    register,
    errors,
    watch,
    getValues,
    setValue,
    isSubmitted,
    setCode,
    setStatus,
    onImageChange: handleImageChange,
    resolveImage,
  };

  return (
    <div className="delivery-note-template-form">
      <Typography
        className="title-3 violet text-uppercase"
        type="h1"
        label={
          <FormattedMessage
            {...(template?.id
              ? DELIVERY_NOTE_CREATE_HEADER.editTitle
              : DELIVERY_NOTE_CREATE_HEADER.title)}
          />
        }
      />
      <Typography
        className="title-5 light-slate-gray"
        type="p"
        label={<FormattedMessage {...DELIVERY_NOTE_CREATE_HEADER.subtitle} />}
      />

      <div className="delivery-note-creation">
        <form
          className="delivery-note-form"
          onSubmit={(e) => e.preventDefault()}
        >
          <Typography
            className="delivery-note-form__notice"
            type="p"
            label={
              <FormattedMessage {...DELIVERY_NOTE_CREATE_HEADER.notice} />
            }
          />
          <IdentityBlock {...blockProps} currencyList={offeredCurrencies} />
          <IssuerBlock {...blockProps} />
          <RecipientBlock {...blockProps} />
          <ColumnsBlock {...blockProps} currencySymbol={currencySymbol} />
          <SignatoriesBlock {...blockProps} />

          <div className="mission-form-buttons">
            <div className="afterButton">
              <Button
                type="button"
                className="btn-small btn-outline"
                onClick={onCancel}
                id="button-form-reset"
              >
                <FormattedMessage id="cancel" defaultMessage="Annuler" />
              </Button>
              <Button
                type="button"
                color="btn-small gray-700"
                onClick={submitForm}
                disabled={isSaving}
                id="button-form-validation"
              >
                <FormattedMessage {...submitLabel} />
              </Button>
            </div>
          </div>
        </form>

        <DeliveryNoteTemplateLivePreview
          control={control}
          resolveImage={resolveImage}
        />
      </div>
    </div>
  );
};

DeliveryNoteTemplateForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSaveStatus: PropTypes.func,
  template: PropTypes.object,
};

export default DeliveryNoteTemplateForm;
